import React, { useContext, useRef, useState } from 'react'
import { FaCheck, FaFileCsv, FaFileExcel } from 'react-icons/fa'
import { Link, useNavigate } from 'react-router'
import styles from './ImAddProducts.module.css'
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { BASE_URL_API } from '../../References/URLs';
import AuthContext from '../../store/auth-context';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import { RxCrossCircled } from "react-icons/rx";
import { toast } from 'react-toastify';
import { MdOutlineFileUpload } from 'react-icons/md';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Download, Eye, Sparkles, UploadCloud } from 'lucide-react';
import { BsInfoCircleFill } from "react-icons/bs";
import { BiLoaderAlt } from 'react-icons/bi';



const REQUIRED_HEADERS = ["productName", "brandName", "mrp", "discount", "priceAfterDiscount", "stock", "minimumStockAlert", "vendorName"];
// behavior toggles
const EXACT_ORDER = true;          // require "serviceName,basePrice" in that order
const ERROR_ON_DUPLICATE_NAMES = true;
const MAX_NAME_LEN = 100;
const MAX_ROWS = 25000;            // safety cap; tune for your needs
const MAX_FILE_SIZE_MB = 10;
// strip BOM/zero-width/NBSP and trim
const clean = (s) =>
    String(s ?? "")
        .replace(/\uFEFF/g, "")   // BOM
        .replace(/\u200B/g, "")   // zero-width
        .replace(/\u00A0/g, " ")  // NBSP -> space
        .trim();

// normalize header *for comparison only* (we keep original for display)
const normalizeHeaderStrict = (h) => clean(h);             // strict: only trim
const normalizeHeaderLoose = (h) => clean(h);

// parse price like "₹1,499.50" or "1,499.50" -> 1499.5
const parsePrice = (v) => {
    if (v === null || v === undefined) return { ok: false, val: null };
    const s = clean(v);
    if (s === "") return { ok: false, val: null };
    // remove currency symbols and spaces; keep digits and dot
    const num = s.replace(/[^\d.]/g, "");
    if (num === "") return { ok: false, val: null };
    // guard multiple dots (e.g., "1.234.56")
    if ((num.match(/\./g) || []).length > 1) return { ok: false, val: null };
    const val = Number(num);
    if (!Number.isFinite(val) || val < 0) return { ok: false, val: null };
    return { ok: true, val };
};


const ImAddProducts = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const vendorIdentifier = JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier
    const [fileData, setFileData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [error, setError] = useState("");
    const fileInputRef = useRef(null);
    const [selectedFileName, setSelectedFileName] = useState('No file selected')
    const [isLoading, setIsLoading] = useState(false)
    const [errorOpen, setErrorOpen] = useState(false)
    const nav = useNavigate()

    // Download sample sheet
    const downloadSampleSheet = () => {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([REQUIRED_HEADERS]);
        XLSX.utils.book_append_sheet(wb, ws, "Sample");
        XLSX.writeFile(wb, "sample_services_upload.xlsx");
    };

    // Download sample sheet as CSV using PapaParse
    const downloadSampleSheetCSV = () => {
        const csv = Papa.unparse([REQUIRED_HEADERS]); // REQUIRED_HEADERS is an array

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "sample_services_upload.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const validateHeaders = (rawHeaders) => {
        // Trim & strip invisibles from each header coming from file
        const headers = rawHeaders.map(normalizeHeaderStrict);

        // reject empties and Excel "__EMPTY" headers if they have any data under them later
        if (headers.some(h => h === "" || /^__EMPTY/i.test(h))) {
            return { ok: false, reason: "One or more header names are empty/invalid." };
        }

        // must be exactly 2 headers
        if (headers.length !== REQUIRED_HEADERS.length) {
            return { ok: false, reason: `Expected exactly ${REQUIRED_HEADERS.join(", ")}, found ${headers.length} columns.` };
        }

        // no duplicates
        const unique = new Set(headers);
        if (unique.size !== headers.length) {
            return { ok: false, reason: "Duplicate header names detected." };
        }

        // exact match (trimmed) and (optionally) exact order
        const expected = REQUIRED_HEADERS.map(normalizeHeaderStrict);

        if (EXACT_ORDER) {
            const sameOrder = headers.every((h, i) => h === expected[i]);
            if (!sameOrder) {
                return { ok: false, reason: `Invalid header order. Expected: ${REQUIRED_HEADERS.join(", ")}` };
            }
        } else {
            const sameSet = headers.every(h => expected.includes(h));
            if (!sameSet) {
                return { ok: false, reason: `Invalid headers. Expected exactly: ${REQUIRED_HEADERS.join(", ")}` };
            }
        }

        return { ok: true, headers };
    };

    const processData = (data, { autoCompute = true } = {}) => {
        if (!Array.isArray(data) || data.length === 0) {
            setError("No data found in the file.");
            setFileData([]);
            setHeaders([]);
            return;
        }

        // Derive raw headers from first row keys
        const rawHeaders = Object.keys(data[0]).map(h => clean(h));
        const missingHeaders = REQUIRED_HEADERS.filter(
            req => !rawHeaders.includes(req)
        );

        if (missingHeaders.length > 0) {
            setError(`Invalid file headers. Missing: ${missingHeaders.join(", ")}`);
            setFileData([]);
            setHeaders([]);
            return;
        }

        const displayHeaders = REQUIRED_HEADERS;

        if (data.length > MAX_ROWS) {
            setError(`Too many rows (${data.length}). Limit is ${MAX_ROWS}.`);
            setFileData([]);
            setHeaders([]);
            return;
        }

        const errors = [];
        const seenNames = new Set();

        const cleanedRows = data
            .map((row, idx) => {
                const line = idx + 1;

                const cleaned = {};
                for (let header of REQUIRED_HEADERS) {
                    cleaned[header] = clean(row[header]);
                }

                // Skip if row is totally blank
                const allEmpty = REQUIRED_HEADERS.every(
                    h => cleaned[h] === "" || cleaned[h] === null
                );
                if (allEmpty) return null;

                // productName validation
                if (!cleaned.productName) {
                    errors.push(`Row ${line}: productName is required.`);
                } else {
                    if (cleaned.productName.length > MAX_NAME_LEN) {
                        errors.push(
                            `Row ${line}: productName exceeds ${MAX_NAME_LEN} characters.`
                        );
                    }
                    if (ERROR_ON_DUPLICATE_NAMES) {
                        if (seenNames.has(cleaned.productName.toLowerCase())) {
                            errors.push(
                                `Row ${line}: Duplicate productName "${cleaned.productName}".`
                            );
                        } else {
                            seenNames.add(cleaned.productName.toLowerCase());
                        }
                    }
                }

                // Parse numbers
                const mrp = parseFloat(cleaned.mrp);
                let discount = cleaned.discount === "" ? null : parseFloat(cleaned.discount);
                let priceAfterDiscount =
                    cleaned.priceAfterDiscount === "" ? null : parseFloat(cleaned.priceAfterDiscount);
                const stock = parseInt(cleaned.stock, 10);
                const minStock = parseInt(cleaned.minimumStockAlert, 10);

                // mrp check
                if (isNaN(mrp) || mrp <= 0) {
                    errors.push(`Row ${line}: mrp is missing or invalid.`);
                }

                // discount / priceAfterDiscount rule
                if (autoCompute) {
                    if ((discount === null || isNaN(discount)) && priceAfterDiscount != null && !isNaN(priceAfterDiscount)) {
                        // compute discount from priceAfterDiscount
                        discount = ((mrp - priceAfterDiscount) / mrp) * 100;
                    } else if ((priceAfterDiscount === null || isNaN(priceAfterDiscount)) && discount != null && !isNaN(discount)) {
                        // compute priceAfterDiscount from discount
                        priceAfterDiscount = mrp - (mrp * discount) / 100;
                    }
                }

                // strict validation if still missing or invalid
                if ((discount === null || isNaN(discount)) &&
                    (priceAfterDiscount === null || isNaN(priceAfterDiscount))) {
                    errors.push(
                        `Row ${line}: Either discount or priceAfterDiscount must be provided.`
                    );
                } else {
                    if (discount !== null && (isNaN(discount) || discount < 0)) {
                        errors.push(`Row ${line}: discount is invalid.`);
                    }
                    if (priceAfterDiscount !== null && (isNaN(priceAfterDiscount) || priceAfterDiscount < 0)) {
                        errors.push(`Row ${line}: priceAfterDiscount is invalid.`);
                    }
                }

                // stock / minStock
                if (isNaN(stock) || stock < 0) {
                    errors.push(`Row ${line}: stock is missing or invalid.`);
                }
                if (isNaN(minStock) || minStock < 0) {
                    errors.push(
                        `Row ${line}: minimumStockAlert is missing or invalid.`
                    );
                }

                return {
                    productName: cleaned.productName,
                    brandName: cleaned.brandName || "",
                    mrp: !isNaN(mrp) ? mrp : null,
                    discount: discount !== null && !isNaN(discount) ? discount : null,
                    priceAfterDiscount:
                        priceAfterDiscount !== null && !isNaN(priceAfterDiscount)
                            ? priceAfterDiscount
                            : null,
                    stock: !isNaN(stock) ? stock : null,
                    minimumStockAlert: !isNaN(minStock) ? minStock : null,
                    vendorName: cleaned.vendorName || "",
                    __invalid__: {
                        productName: !cleaned.productName,
                        mrp: isNaN(mrp),
                        discount: discount === null && priceAfterDiscount === null,
                        priceAfterDiscount: discount === null && priceAfterDiscount === null,
                        stock: isNaN(stock),
                        minimumStockAlert: isNaN(minStock)
                    }
                };
            })
            .filter(Boolean);

        if (cleanedRows.length === 0) {
            setError("All rows are blank.");
            setFileData([]);
            setHeaders([]);
            return;
        }

        if (errors.length > 0) {
            setError(errors);
        } else {
            setError("");
        }

        setHeaders(displayHeaders);
        setFileData(cleanedRows);
    };

    // CSV
    const handleCsv = (file) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: "greedy",
            worker: true,
            complete: (res) => {
                // Post-process headers
                const cleanedData = res.data.map(row =>
                    Object.fromEntries(
                        Object.entries(row).map(([k, v]) => [clean(k), typeof v === "string" ? clean(v) : v])
                    )
                );
                processData(cleanedData);
            },
            error: () => setError("Failed to parse CSV. Please check the file."),
        });
    };

    // Excel
    const handleXlsx = (file) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: "binary" });

            // pick first non-empty sheet
            const sheetName = wb.SheetNames.find((n) => {
                const ws = wb.Sheets[n];
                return ws && ws["!ref"];
            });
            if (!sheetName) {
                setError("No readable sheet found in workbook.");
                return;
            }

            const ws = wb.Sheets[sheetName];

            // Convert to JSON with header row; keep empties as ""
            const jsonData = XLSX.utils.sheet_to_json(ws, {
                defval: "",
                raw: false // ensures numeric-looking strings stay strings (we parse ourselves)
            });

            // As a guard against Excel's __EMPTY headers: if headers look empty, call it out early
            if (jsonData[0] && Object.keys(jsonData[0]).some(k => k === "" || /^__EMPTY/.test(k))) {
                setError("Invalid or empty header cells detected. Please ensure the first row has exactly: serviceName, basePrice.");
                setFileData([]);
                setHeaders([]);
                return;
            }

            processData(jsonData);
        };
        reader.readAsBinaryString(file);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setSelectedFileName(file.name)

        const sizeMb = file.size / (1024 * 1024);
        if (sizeMb > MAX_FILE_SIZE_MB) {
            setError(`File is too large (${sizeMb.toFixed(1)} MB). Max allowed is ${MAX_FILE_SIZE_MB} MB.`);
            return;
        }

        const ext = file.name.split(".").pop()?.toLowerCase();
        if (ext === "csv") return handleCsv(file);
        if (ext === "xlsx" || ext === "xls") return handleXlsx(file);

        setError("Please upload a CSV or Excel (.xlsx/.xls) file.");
    };

    const uploadInBulk = async () => {
        // return;
        if (window.confirm(`Are you sure to upload ${fileData?.length} product(s) in bulk?`)) {
            setIsLoading(true)
            const response = await fetch(BASE_URL_API + "addProductsInBulk",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore?.storeIdentifier,
                        data: fileData,
                        storeName: activeStore?.storeName,
                        vendorIdentifier: vendorIdentifier
                    })
                });

            if (!response.ok) {
                console.log("Something went wrong : Server Error")
            } else {
                const respo = await response.json()
                if (respo?.status === "success") {
                    toast('Products has been successfully uploaded!')
                    nav(`/manage/inventory/products`)
                } else {
                    if (respo?.message === "tokenExpired") {
                        authCtx.logout()
                    }
                }
                console.log(respo)
            }
            setIsLoading(false)
        }
        console.log(fileData)
    }

    console.log(error)

    const hasErrors = Array.isArray(error) ? error.length > 0 : Boolean(error)
    const errorList = Array.isArray(error) ? error : (error ? [error] : [])

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.mainHeader}>
                <div className={styles.mainTitleBlock}>
                    <div className={styles.statusPill}>Bulk Upload</div>
                    <div className={styles.mainTitle}>Upload Products</div>
                    <div className={styles.mainSubtitle}>Use CSV or Excel templates to import products faster with instant validation preview.</div>
                </div>
                <div className={styles.addProduct}>
                    <Link className={styles.addProductIcon} to={`/manage/inventory/products`}>
                        <Eye size={15} /> View Products
                    </Link>
                </div>
            </div>

            <div className={styles.stepsWrapper} >
                <div className={styles.stepsContainer}>
                    <div className={styles.downloadTemplateStep} >
                        <div className={styles.stepTitleWrap}>
                            <div className={styles.stepIcon}><Download size={16} /></div>
                            <div className={styles.stepTitle}>Download Template</div>
                        </div>
                        <div className={styles.downloadBtnWrapper} >
                            <button className={styles.downloadBtn} onClick={() => downloadSampleSheetCSV()} >
                                <FaFileCsv color="#8d959e" /> Download CSV
                            </button>
                            <button className={styles.downloadBtn} onClick={() => downloadSampleSheet()} >
                                <FaFileExcel color="#8d959e" /> Download Excel
                            </button>
                        </div>
                    </div>
                    <div className={styles.uploadFileStep} >
                        <div className={styles.stepTitleWrap}>
                            <div className={styles.stepIcon}><UploadCloud size={16} /></div>
                            <div className={styles.stepTitle}>Upload File</div>
                        </div>
                        <div className={styles.downloadBtnWrapper} >
                            <div className={styles.uploadInputShell} >
                                <input
                                    id="inventoryBulkFileInput"
                                    className={styles.hiddenFileInput}
                                    type="file"
                                    accept=".csv, .xlsx, .xls"
                                    onChange={handleFileUpload}
                                    ref={fileInputRef}
                                />
                                <label htmlFor="inventoryBulkFileInput" className={styles.chooseFileBtn}>
                                    <UploadCloud size={14} /> Choose File
                                </label>
                                <span className={styles.fileNameText} title={selectedFileName}>
                                    {selectedFileName}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>



            <div className={styles.previewWrapper} >
                {fileData?.length > 0 &&
                    <div className={styles.previewContainer} >
                        {hasErrors ?
                            <div className={styles.errorWrapper}>
                                <div className={styles.errorMessage} onClick={() => setErrorOpen(!errorOpen)} >
                                    <AlertTriangle size={16} /> {errorList.length} error(s) found!
                                    {errorOpen === true ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                                </div>
                                {errorOpen &&
                                    <div className={styles.errors}>
                                        {errorList.map((item, index) => (
                                            <div key={index}>
                                                {item}
                                            </div>
                                        ))}
                                    </div>
                                }
                            </div>
                            :
                            <div className={styles.errorWrapper}>
                                <div className={styles.errorMessage} >
                                    <CheckCircle2 size={16} /> Data validated successfully
                                </div>
                            </div>
                        }
                        <div>
                            {hasErrors ?
                                <button onClick={() => alert(`Please fix all errors before uploading!`)} className={styles.confirmUploadDisable} >
                                    <MdOutlineFileUpload size={18} /> Confirm Upload
                                </button>
                                :
                                <>
                                    {isLoading === true ?
                                        <button onClick={() => uploadInBulk()} className={styles.confirmUpload} >
                                            <BiLoaderAlt size={18} className={styles.spinner} />Uploading ...
                                        </button>
                                        :
                                        <button onClick={() => uploadInBulk()} className={styles.confirmUpload} >
                                            <MdOutlineFileUpload size={18} /> Confirm Upload
                                        </button>
                                    }
                                </>
                            }
                        </div>
                    </div>
                }

                {fileData?.length > 0 ?
                    <div className={styles.mainBodyWrapper}>
                        <div className={styles.mainBodyContainer}>
                            <div className={styles.tableHeader}>
                                <div className={styles.sn} >
                                    #
                                </div>
                                <div className={styles.productName}>
                                    Product
                                </div>
                                <div className={styles.barcode} >
                                    SKU / Barcode
                                </div>
                                <div className={styles.mrp}>
                                    MRP
                                </div>
                                <div className={styles.price}>
                                    S. P.
                                </div>
                                <div className={styles.discount}>
                                    Discount (%)
                                </div>
                                <div className={styles.stock} >
                                    Stock
                                </div>
                                <div className={styles.expiry} >
                                    Min Stock
                                </div>
                            </div>
                            <div className={styles.productWrapper}>
                                {fileData?.map((item, index) => (
                                    <div key={index} className={styles.eachRowWrapper} >
                                        <div className={styles.sn} >
                                            {index + 1}.
                                        </div>
                                        <div className={`${styles.productName} ${item?.__invalid__?.productName ? styles.errorRowTxt : ""}`}>
                                            {item?.productName}
                                        </div>
                                        <div className={styles.barcode}>

                                        </div>
                                        <div className={`${styles.mrp} ${item?.__invalid__?.mrp ? styles.errorRowTxt : ""}`}>
                                            ₹{item?.mrp}
                                        </div>
                                        <div className={`${styles.price} ${item?.__invalid__?.priceAfterDiscount ? styles.errorRowTxt : ""}`}>
                                            ₹{item.priceAfterDiscount?.toFixed(2)}
                                        </div>
                                        <div className={styles.discount}>
                                            {item?.discount?.toFixed(2)}%
                                        </div>
                                        <div className={styles.stock}>
                                            {item?.stock}
                                        </div>

                                        <div className={styles.expiry}>
                                            {item?.minimumStockAlert}
                                        </div>
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>
                    :
                    <div className={styles.mainBodyWrapper}>
                        <div className={styles.notDataWrapper} >
                            <div>
                                <Sparkles size={40} color="#c4b5fd" />
                            </div>
                            <div className={styles.text} >
                                No Preview Data found! Please select data to preview!
                            </div>
                        </div>
                    </div>
                }
            </div>


        </div>
    )
}

export default ImAddProducts